// Standalone vendor marketplace at /marketplace.
// Buyers and sellers can browse all vendor categories, view profiles, and
// send a direct inquiry — without needing to be inside a specific flow stage.

import { useEffect, useState } from "react";

const BETA_SECRET = process.env.REACT_APP_BETA_SECRET || "sixzero2026";
const BETA_KEY    = "od_beta_token";

const CATEGORIES = [
  {
    id: "prepare",
    label: "Prepare & Improve",
    icon: "🔨",
    types: ["stager","handyman","painter","cleaner","landscaper","electrician","plumber","hvac","roofer","general_contractor"],
  },
  {
    id: "media",
    label: "Photography & Media",
    icon: "📷",
    types: ["photographer","videographer"],
  },
  {
    id: "inspect",
    label: "Inspection & Assessment",
    icon: "🔍",
    types: ["inspector","pest_inspector","appraiser"],
  },
  {
    id: "finance",
    label: "Finance & Legal",
    icon: "📋",
    types: ["lender","real_estate_attorney","notary","title_escrow"],
  },
  {
    id: "professionals",
    label: "Professionals",
    icon: "🏡",
    types: ["listing_assistant"],
  },
  {
    id: "moving",
    label: "Moving & Storage",
    icon: "📦",
    types: ["mover","storage"],
  },
  {
    id: "design",
    label: "Design",
    icon: "✏️",
    types: ["interior_designer"],
  },
];

const TYPE_LABELS = {
  stager: "Stager",
  handyman: "Handyman",
  painter: "Painter",
  cleaner: "Cleaner",
  landscaper: "Landscaper",
  electrician: "Electrician",
  plumber: "Plumber",
  hvac: "HVAC",
  roofer: "Roofer",
  general_contractor: "General Contractor",
  photographer: "Photographer",
  videographer: "Videographer / 3D Tour",
  inspector: "Home Inspector",
  pest_inspector: "Pest Inspector",
  appraiser: "Appraiser",
  lender: "Lender",
  real_estate_attorney: "Real Estate Attorney",
  notary: "Notary",
  title_escrow: "Title / Escrow",
  listing_assistant: "Listing Assistant",
  mover: "Mover",
  storage: "Storage",
  interior_designer: "Interior Designer",
};

const fmtMoney = (n) => n == null ? null : `$${Math.round(n).toLocaleString()}`;

const CSS = `
  *{box-sizing:border-box}
  body{margin:0;font-family:'Inter','Outfit',sans-serif;background:#eef3ef;color:#152d21}
  .mp-page{min-height:100vh;display:flex;flex-direction:column}

  /* nav */
  .mp-top{display:flex;align-items:center;gap:14px;padding:12px 22px;border-bottom:1px solid rgba(45,122,98,0.15);background:#fff;flex-wrap:wrap}
  .mp-brand{display:flex;align-items:center;gap:10px;cursor:pointer}
  .mp-brand-name{font-family:'DM Mono',monospace;font-size:13px;font-weight:700;color:#2b8259;letter-spacing:0.2em}
  .mp-top-links{margin-left:auto;display:flex;gap:8px}
  .mp-link{background:none;border:1px solid rgba(45,122,98,0.2);color:#5a8a78;border-radius:7px;padding:5px 10px;font-size:11px;cursor:pointer;font-family:'DM Mono',monospace}
  .mp-link:hover{background:#1c2b25;color:#d4f5e8;border-color:#1c2b25}

  /* hero */
  .mp-hero{padding:48px 22px 28px;max-width:860px;margin:0 auto;width:100%}
  .mp-pill{display:inline-flex;align-items:center;gap:7px;background:#e1eee6;border:1px solid #b2d4c0;border-radius:20px;padding:5px 14px;margin-bottom:18px}
  .mp-pill-dot{width:6px;height:6px;border-radius:50%;background:#2b8259}
  .mp-pill span{font-family:'DM Mono',monospace;font-size:10px;letter-spacing:0.14em;color:#2b8259;font-weight:700;text-transform:uppercase}
  .mp-h1{font-family:'Playfair Display',serif;font-size:clamp(28px,4.5vw,42px);font-weight:700;color:#152d21;line-height:1.1;margin:0 0 12px;letter-spacing:-0.02em}
  .mp-h1 em{color:#2b8259;font-style:italic;font-weight:500}
  .mp-lead{font-size:15px;color:#5b7365;line-height:1.7;max-width:540px;margin:0}

  /* search/filter bar */
  .mp-bar{padding:0 22px 22px;max-width:860px;margin:0 auto;width:100%;display:flex;gap:10px;flex-wrap:wrap}
  .mp-search{flex:1;min-width:200px;background:#fff;border:1.5px solid #d8ede6;color:#1c2b25;border-radius:10px;padding:11px 14px;font-size:14px;font-family:'Outfit',sans-serif;outline:none}
  .mp-search:focus{border-color:#2d7a62}
  .mp-search::placeholder{color:#9bbfb2}
  .mp-cat-btn{background:#fff;border:1.5px solid #d8ede6;color:#5b7365;border-radius:10px;padding:10px 14px;font-size:13px;cursor:pointer;font-family:'Outfit',sans-serif;white-space:nowrap;transition:all .15s}
  .mp-cat-btn:hover,.mp-cat-btn.active{background:#1c2b25;border-color:#1c2b25;color:#d4f5e8}

  /* body */
  .mp-body{flex:1;padding:0 22px 60px;max-width:860px;margin:0 auto;width:100%}

  /* category section */
  .mp-cat-label{display:flex;align-items:center;gap:8px;font-size:11px;font-weight:700;color:#5b7365;letter-spacing:0.1em;text-transform:uppercase;margin:28px 0 12px;padding-bottom:8px;border-bottom:1px solid #d8ede6}
  .mp-cat-icon{font-size:16px}

  /* vendor grid */
  .mp-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:14px}

  /* vendor card */
  .mp-vcard{background:#fff;border:1.5px solid rgba(45,122,98,0.1);border-radius:16px;padding:18px;cursor:pointer;transition:all .15s;text-align:left;width:100%}
  .mp-vcard:hover{border-color:#2d7a62;box-shadow:0 6px 20px rgba(28,43,37,0.1);transform:translateY(-2px)}
  .mp-vcard.selected{border-color:#2d7a62;box-shadow:0 6px 20px rgba(43,130,89,0.15)}
  .mp-vcard-header{display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:8px}
  .mp-vcard-name{font-weight:700;font-size:14px;color:#152d21;line-height:1.3}
  .mp-vcard-logo{width:32px;height:32px;border-radius:8px;object-fit:cover;flex-shrink:0;border:1px solid #e1ede7}
  .mp-vcard-logo-fallback{display:flex;align-items:center;justify-content:center;background:#eef5f1;color:#7aad98;font-family:'DM Mono',monospace;font-size:13px;font-weight:700}
  .mp-detail-logo{width:52px;height:52px;border-radius:12px;object-fit:cover;flex-shrink:0;border:1px solid #e1ede7}
  .mp-vcard-rating{font-family:'DM Mono',monospace;font-size:11px;color:#c47c1a;white-space:nowrap;flex-shrink:0}
  .mp-vcard-type{font-family:'DM Mono',monospace;font-size:10px;color:#2b8259;letter-spacing:0.08em;text-transform:uppercase;margin-bottom:6px}
  .mp-vcard-blurb{font-size:12px;color:#5b7365;line-height:1.5;margin-bottom:10px}
  .mp-vcard-meta{display:flex;gap:10px;flex-wrap:wrap;font-family:'DM Mono',monospace;font-size:11px;color:#9aa9a0;border-top:1px solid #f0f4f2;padding-top:8px}

  /* detail panel */
  .mp-detail{background:#fff;border:1.5px solid rgba(45,122,98,0.1);border-radius:18px;padding:28px;margin-top:24px;box-shadow:0 6px 26px rgba(28,43,37,0.06)}
  .mp-detail-header{display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:12px;margin-bottom:16px}
  .mp-detail-name{font-family:'Playfair Display',serif;font-size:22px;font-weight:700;color:#152d21;margin:0}
  .mp-detail-type{font-family:'DM Mono',monospace;font-size:11px;color:#2b8259;letter-spacing:0.1em;text-transform:uppercase;margin-top:4px}
  .mp-detail-close{background:none;border:1px solid #d8ede6;color:#5b7365;border-radius:8px;padding:6px 12px;font-size:12px;cursor:pointer;font-family:'DM Mono',monospace}
  .mp-detail-close:hover{background:#f7faf8}
  .mp-detail-blurb{font-size:14px;color:#5b7365;line-height:1.7;margin-bottom:16px}
  .mp-detail-pricing{background:#f7faf8;border:1px solid #e8f0ec;border-radius:10px;padding:14px;margin-bottom:16px}
  .mp-detail-pricing-label{font-family:'DM Mono',monospace;font-size:10px;color:#5b7365;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:8px}
  .mp-pricing-row{display:flex;justify-content:space-between;padding:5px 0;font-size:13px;border-bottom:1px solid #f0f4f2}
  .mp-pricing-row:last-child{border-bottom:none}
  .mp-pricing-key{color:#5b7365;text-transform:capitalize}
  .mp-pricing-val{font-family:'DM Mono',monospace;color:#1c5448;font-weight:600}

  /* inquiry form */
  .mp-inq{margin-top:20px;padding-top:20px;border-top:1px solid #eef3ef}
  .mp-inq-title{font-size:14px;font-weight:700;color:#152d21;margin-bottom:14px}
  .mp-inq-row{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px}
  .mp-inq-row.single{grid-template-columns:1fr}
  .mp-label{display:block;font-size:11px;font-weight:700;color:#5b7365;letter-spacing:0.08em;text-transform:uppercase;margin-bottom:5px}
  .mp-input,.mp-textarea{width:100%;background:#f7faf8;border:1.5px solid #d8ede6;color:#1c2b25;border-radius:10px;padding:10px 13px;font-size:13px;font-family:'Outfit',sans-serif;outline:none}
  .mp-input:focus,.mp-textarea:focus{border-color:#2d7a62}
  .mp-input::placeholder,.mp-textarea::placeholder{color:#9bbfb2}
  .mp-textarea{min-height:80px;resize:vertical;line-height:1.55}
  .mp-submit{background:#2b8259;color:#fff;border:none;border-radius:12px;padding:12px 24px;font-size:14px;font-weight:700;cursor:pointer;font-family:'Outfit',sans-serif;box-shadow:0 4px 14px rgba(43,130,89,0.24);width:100%;margin-top:4px}
  .mp-submit:hover:not(:disabled){background:#1c5448}
  .mp-submit:disabled{opacity:0.6;cursor:default;box-shadow:none}
  .mp-err{color:#c0354a;font-size:12px;font-family:'DM Mono',monospace;margin-top:8px}
  .mp-ok{color:#2d7a62;font-size:13px;font-family:'DM Mono',monospace;margin-top:8px;padding:10px 14px;background:#e8f7f0;border-radius:8px;border:1px solid #b4dfc8}

  .mp-empty{padding:40px 20px;text-align:center;color:#5b7365;font-style:italic}

  @media(max-width:640px){
    .mp-hero{padding:32px 16px 20px}
    .mp-bar,.mp-body{padding-left:16px;padding-right:16px}
    .mp-grid{grid-template-columns:1fr}
    .mp-inq-row{grid-template-columns:1fr}
    .mp-detail{padding:20px 16px}
  }
`;

const Logo = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden="true">
    <rect width="40" height="40" rx="10" fill="#1c2b25" />
    <path d="M20 8L7 18V33H16V25H24V33H33V18L20 8Z" fill="#7ecfb0" />
    <rect x="17" y="25" width="6" height="8" fill="#1c2b25" opacity="0.4" />
  </svg>
);

function InquiryForm({ vendor, onClose }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "", property_address: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const update = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.name.trim()) { setErr("Name required."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) { setErr("Valid email required."); return; }
    setBusy(true);
    try {
      const res = await fetch("/api/vendors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "inquire",
          vendor_id: vendor.id,
          name: f.name,
          email: f.email,
          phone: f.phone || null,
          message: f.message || null,
          property_address: f.property_address || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `${res.status}`);
      setDone(true);
    } catch (e) {
      setErr(e.message || "Couldn't send. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="mp-ok">
        ✓ Inquiry sent to {vendor.name}. They'll reach out at {f.email}.
        <button type="button" onClick={onClose} style={{ marginLeft: 12, background: "none", border: "none", color: "#2d7a62", cursor: "pointer", fontFamily: "'DM Mono',monospace", fontSize: 11, textDecoration: "underline" }}>
          close
        </button>
      </div>
    );
  }

  return (
    <div className="mp-inq">
      <div className="mp-inq-title">Contact {vendor.name}</div>
      <form onSubmit={submit}>
        <div className="mp-inq-row">
          <div>
            <label className="mp-label">Your name *</label>
            <input className="mp-input" value={f.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Smith" required />
          </div>
          <div>
            <label className="mp-label">Email *</label>
            <input className="mp-input" type="email" value={f.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" required />
          </div>
        </div>
        <div className="mp-inq-row">
          <div>
            <label className="mp-label">Phone (optional)</label>
            <input className="mp-input" value={f.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(415) 555-0100" />
          </div>
          <div>
            <label className="mp-label">Property address (optional)</label>
            <input className="mp-input" value={f.property_address} onChange={(e) => update("property_address", e.target.value)} placeholder="123 Main St, SF CA 94110" />
          </div>
        </div>
        <div className="mp-inq-row single">
          <div>
            <label className="mp-label">Message (optional)</label>
            <textarea className="mp-textarea" value={f.message} onChange={(e) => update("message", e.target.value)} placeholder="What are you looking for? Any timing constraints?" maxLength={1000} />
          </div>
        </div>
        <button type="submit" className="mp-submit" disabled={busy}>
          {busy ? "Sending…" : `Send inquiry to ${vendor.name} →`}
        </button>
        {err && <div className="mp-err">{err}</div>}
      </form>
    </div>
  );
}

function VendorDetail({ vendor, onClose }) {
  const [showInquiry, setShowInquiry] = useState(false);
  const pricing = vendor.base_pricing && Object.keys(vendor.base_pricing).length > 0
    ? vendor.base_pricing : null;

  return (
    <div className="mp-detail">
      <div className="mp-detail-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {vendor.photo_url ? (
            <img src={vendor.photo_url} alt={vendor.name} className="mp-detail-logo" />
          ) : (
            <div className="mp-detail-logo mp-vcard-logo-fallback">{(vendor.name || "?").slice(0, 1).toUpperCase()}</div>
          )}
          <div>
            <h2 className="mp-detail-name">{vendor.name}</h2>
            <div className="mp-detail-type">{TYPE_LABELS[vendor.type] || vendor.type}</div>
            {vendor.rating != null && (
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 12, color: "#c47c1a", marginTop: 4 }}>
                ★ {vendor.rating} · {vendor.review_count || 0} reviews
              </div>
            )}
          </div>
        </div>
        <button type="button" className="mp-detail-close" onClick={onClose}>✕ close</button>
      </div>

      {vendor.blurb && (
        <div className="mp-detail-blurb">{vendor.blurb}</div>
      )}

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16, fontFamily: "'DM Mono',monospace", fontSize: 12, color: "#5b7365" }}>
        {vendor.lead_time_days != null && (
          <span>⏱ {vendor.lead_time_days}-day lead time</span>
        )}
        {vendor.service_area_zips?.length > 0 && (
          <span>📍 {vendor.service_area_zips.slice(0, 5).join(", ")}{vendor.service_area_zips.length > 5 ? " +" + (vendor.service_area_zips.length - 5) + " more" : ""}</span>
        )}
      </div>

      {pricing && (
        <div className="mp-detail-pricing">
          <div className="mp-detail-pricing-label">Base pricing</div>
          {Object.entries(pricing).map(([k, v]) => (
            <div key={k} className="mp-pricing-row">
              <span className="mp-pricing-key">{k.replace(/_/g, " ")}</span>
              <span className="mp-pricing-val">{fmtMoney(v) || v}</span>
            </div>
          ))}
        </div>
      )}

      {!showInquiry && (
        <button
          type="button"
          className="mp-submit"
          onClick={() => setShowInquiry(true)}
          style={{ marginTop: 0 }}
        >
          Contact this vendor →
        </button>
      )}

      {showInquiry && (
        <InquiryForm vendor={vendor} onClose={() => { setShowInquiry(false); onClose(); }} />
      )}
    </div>
  );
}

export default function MarketplaceApp() {
  // Beta gate — same as the rest of the app.
  if (typeof window !== "undefined" && localStorage.getItem(BETA_KEY) !== BETA_SECRET) {
    window.location.href = "/public";
    return null;
  }

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState(null); // null = all
  const [selected, setSelected] = useState(null); // vendor object

  useEffect(() => {
    setLoading(true);
    fetch("/api/vendors", {
      headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
    })
      .then((r) => r.ok ? r.json() : r.json().then((d) => Promise.reject(new Error(d.error || r.status))))
      .then((d) => setVendors(d.rows || []))
      .catch((e) => setErr(e.message || "Couldn't load vendors."))
      .finally(() => setLoading(false));
  }, []);

  const q = search.toLowerCase();
  const visibleVendors = vendors.filter((v) => {
    if (activeCat) {
      const cat = CATEGORIES.find((c) => c.id === activeCat);
      if (cat && !cat.types.includes(v.type)) return false;
    }
    if (q) {
      return (
        v.name?.toLowerCase().includes(q) ||
        v.blurb?.toLowerCase().includes(q) ||
        (TYPE_LABELS[v.type] || v.type).toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Group by category for display
  const grouped = CATEGORIES.map((cat) => ({
    ...cat,
    vendors: visibleVendors.filter((v) => cat.types.includes(v.type)),
  })).filter((cat) => cat.vendors.length > 0);

  return (
    <div className="mp-page">
      <style>{CSS}</style>

      <header className="mp-top">
        <div className="mp-brand" onClick={() => (window.location.href = "/")}>
          <Logo />
          <div className="mp-brand-name">6zero.ai</div>
        </div>
        <div className="mp-top-links">
          <button type="button" className="mp-link" onClick={() => (window.location.href = "/sell/v2")}>Sell</button>
          <button type="button" className="mp-link" onClick={() => (window.location.href = "/buy")}>Buy</button>
          <button type="button" className="mp-link" onClick={() => (window.location.href = "/join")}>List your services</button>
        </div>
      </header>

      <section className="mp-hero">
        <div className="mp-pill"><span className="mp-pill-dot" /><span>Marketplace</span></div>
        <h1 className="mp-h1">Every pro you need,<br /><em>in one place.</em></h1>
        <p className="mp-lead">
          From stagers and photographers to lenders, inspectors, and movers — browse vetted
          vendors and send a direct inquiry. All professionals are pre-screened by 6zero.ai.
        </p>
      </section>

      <div className="mp-bar">
        <input
          className="mp-search"
          type="search"
          placeholder="Search vendors…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setSelected(null); }}
        />
        <button
          type="button"
          className={`mp-cat-btn${!activeCat ? " active" : ""}`}
          onClick={() => { setActiveCat(null); setSelected(null); }}
        >
          All
        </button>
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`mp-cat-btn${activeCat === c.id ? " active" : ""}`}
            onClick={() => { setActiveCat(c.id); setSelected(null); }}
          >
            {c.icon} {c.label}
          </button>
        ))}
      </div>

      <main className="mp-body">
        {loading && (
          <div className="mp-empty">Loading vendors…</div>
        )}
        {err && (
          <div className="mp-empty" style={{ color: "#c0354a" }}>{err}</div>
        )}

        {selected && (
          <VendorDetail vendor={selected} onClose={() => setSelected(null)} />
        )}

        {!loading && !err && grouped.length === 0 && (
          <div className="mp-empty">No vendors match your search.</div>
        )}

        {!loading && !err && grouped.map((cat) => (
          <section key={cat.id}>
            <div className="mp-cat-label">
              <span className="mp-cat-icon">{cat.icon}</span>
              {cat.label}
              <span style={{ marginLeft: "auto", fontWeight: 400, color: "#9aa9a0" }}>{cat.vendors.length}</span>
            </div>
            <div className="mp-grid">
              {cat.vendors.map((v) => {
                const prices = v.base_pricing ? Object.values(v.base_pricing).filter((x) => typeof x === "number") : [];
                const minPrice = prices.length ? Math.min(...prices) : null;
                return (
                  <button
                    key={v.id}
                    type="button"
                    className={`mp-vcard${selected?.id === v.id ? " selected" : ""}`}
                    onClick={() => setSelected(selected?.id === v.id ? null : v)}
                  >
                    <div className="mp-vcard-header">
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        {v.photo_url ? (
                          <img src={v.photo_url} alt={v.name} className="mp-vcard-logo" />
                        ) : (
                          <div className="mp-vcard-logo mp-vcard-logo-fallback">{(v.name || "?").slice(0, 1).toUpperCase()}</div>
                        )}
                        <div className="mp-vcard-name">{v.name}</div>
                      </div>
                      {v.rating != null && (
                        <div className="mp-vcard-rating">★ {v.rating}</div>
                      )}
                    </div>
                    <div className="mp-vcard-type">{TYPE_LABELS[v.type] || v.type}</div>
                    {v.blurb && (
                      <div className="mp-vcard-blurb">{v.blurb.slice(0, 120)}{v.blurb.length > 120 ? "…" : ""}</div>
                    )}
                    <div className="mp-vcard-meta">
                      {v.lead_time_days != null && <span>{v.lead_time_days}d lead</span>}
                      {minPrice != null && <span>from {fmtMoney(minPrice)}</span>}
                      {v.review_count > 0 && <span>{v.review_count} reviews</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
